import type { ReactNode } from "react";

/**
 * The line icons the audit-day banner and the step bar draw, inline.
 *
 * **Inline SVG and not an icon package.** Between them the two need a handful of glyphs; a
 * dependency for a handful of glyphs is a dependency, a build step and a set of licence
 * terms for a handful of glyphs.
 * They are drawn on the Lucide grid — 24×24, 2px round strokes — so they sit with the
 * chevrons and marks already hand-drawn elsewhere in this app.
 *
 * Every one is `aria-hidden`. The controls they sit in carry the label: an icon-only
 * button has `aria-label` and `title`, and a labelled button has the words beside it.
 * An icon that announced itself would double every one of those.
 */
function Glyph({ size = 15, children }: { size?: number; children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

/** The audit clock. */
export function TimerIcon(props: { size?: number }) {
  return (
    <Glyph {...props}>
      <line x1="10" y1="2" x2="14" y2="2" />
      <line x1="12" y1="14" x2="15" y2="11" />
      <circle cx="12" cy="14" r="8" />
    </Glyph>
  );
}

/** Export chat — a copy of the record, taken away. */
export function DownloadIcon(props: { size?: number }) {
  return (
    <Glyph {...props}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </Glyph>
  );
}

/** Restart the audit day — the one control here that erases rather than records. */
export function RestartIcon(props: { size?: number }) {
  return (
    <Glyph {...props}>
      <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
      <polyline points="3 3 3 8 8 8" />
    </Glyph>
  );
}

/** Auditor controls — the tray of interventions. */
export function ControlsIcon(props: { size?: number }) {
  return (
    <Glyph {...props}>
      <line x1="21" y1="6" x2="3" y2="6" />
      <line x1="21" y1="12" x2="3" y2="12" />
      <line x1="21" y1="18" x2="3" y2="18" />
      <circle cx="9" cy="6" r="2" />
      <circle cx="15" cy="12" r="2" />
      <circle cx="7" cy="18" r="2" />
    </Glyph>
  );
}

/** Back — the way out of the session. */
export function BackIcon(props: { size?: number }) {
  return (
    <Glyph {...props}>
      <polyline points="15 18 9 12 15 6" />
    </Glyph>
  );
}

/**
 * A step the audit has passed. Drawn in the step bar's mark, never beside a grade — a tick
 * against a finding would read as a conformity, and conformity here is a determination a
 * human signs rather than a state machine's own progress.
 */
export function CheckIcon(props: { size?: number }) {
  return (
    <Glyph {...props}>
      <polyline points="20 6 9 17 4 12" />
    </Glyph>
  );
}

/**
 * Reachable but not writable — the step bar's read-only marker.
 *
 * It sits after the step's name rather than replacing anything, because *which* step a
 * reader is looking at and *whether they may write there* are two facts and the second is
 * the smaller one. A closed padlock is the one shape that says look-but-do-not-touch
 * without a sentence; the sentence is still there, in the segment's `title`.
 */
export function LockIcon(props: { size?: number }) {
  return (
    <Glyph {...props}>
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </Glyph>
  );
}

/**
 * Dismiss — the cross on the tally's finding panel.
 *
 * A panel opened from a segment of the banner is closed from its own corner: an audit-day
 * reader who opened it mid-transcript should not have to find the segment again to put it
 * away. Escape and a click outside do the same thing; this is the visible one.
 */
export function CloseIcon(props: { size?: number }) {
  return (
    <Glyph {...props}>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </Glyph>
  );
}
